import React, { memo, useCallback } from 'react';
import { Alert, Icon, Modal, Spin, Tag } from 'antd';
import { shallowEqual, useDispatch, useSelector } from 'react-redux';
import { shortFilterName } from '../../../../../../helpers/filterName';
import { ReactComponent as FilterIcon } from '../../../../../../resource/FilterDocumentIcon.svg';
import setDefaultFilter from '../../../api/setDefaultFilter';
import { filterActions } from '../../../model/actions';
import { filterConstants } from '../../../model/constants';
import { useRequestFilterCommands } from '../../../model/commandsContext';
import { requestFilterSectionIds } from '../../../model/sectionExpansion';
import FilterSection from '../../primitives/FilterSection';
import styles from './SavedFiltersSection.module.scss';

const { confirm } = Modal;

const selectSavedFiltersState = (state) => {
  const filter = state.filterStore;

  return {
    filters: filter.savedList,
    selected: filter.selected,
    loading: filter.readingList,
    loadFailed: filter.readListFail,
    saveFailed: filter.saveFail,
    deleting: filter.deleting,
  };
};

const SavedFiltersSection = () => {
  const dispatch = useDispatch();
  const filterCommands = useRequestFilterCommands();
  const { filters, selected, loading, loadFailed, saveFailed, deleting } =
    useSelector(selectSavedFiltersState, shallowEqual);

  const handleSelect = useCallback(
    (filter) => {
      dispatch(filterCommands.setSavedFilter({ filter }));
      setDefaultFilter(filter.id);
    },
    [dispatch, filterCommands]
  );

  const handleDelete = useCallback(
    (event, filter) => {
      event.preventDefault();
      event.stopPropagation();

      confirm({
        title: 'Удалить фильтр?',
        content: <strong>{filter.name}</strong>,
        onOk: async () => {
          await dispatch(filterActions.deleteFilter({ id: filter.id }));
          dispatch(filterActions.getList());
        },
        onCancel: () => {},
      });
    },
    [dispatch]
  );

  return (
    <FilterSection
      sectionId={requestFilterSectionIds.savedFilters}
      heading="Сохраненные фильтры"
      description="Быстрый доступ к вашим наборам параметров"
      summary={
        selected && (
          <span className={styles.selectedFilter}>
            Выбран: {shortFilterName(selected.name)}
          </span>
        )
      }
      icon={<FilterIcon />}
    >
      {saveFailed && (
        <Alert
          className={styles.alert}
          message="Не удалось сохранить фильтр"
          type="error"
          closable={true}
          onClose={() => {
            dispatch({ type: filterConstants.CLEAR_SAVE_FILTER_ERROR });
          }}
        />
      )}

      <Spin
        size="small"
        spinning={loading || deleting}
      >
        {loadFailed ? (
          <Alert
            message="Не удалось загрузить сохранённые фильтры"
            type="error"
          />
        ) : (
          <div className={styles.tags}>
            {filters?.length ? (
              filters.map((filter) => {
                const active = selected?.id === filter.id;

                return (
                  <Tag
                    key={filter.id}
                    color={active ? '#0995ed' : '#e9e9e9'}
                    className={styles.tag}
                    closable={true}
                    onClick={() => handleSelect(filter)}
                    onClose={(event) => handleDelete(event, filter)}
                  >
                    <span className={active ? styles.activeText : undefined}>
                      {shortFilterName(filter.name)}
                    </span>
                  </Tag>
                );
              })
            ) : (
              <div className={styles.empty}>
                <Icon type="info-circle" /> Сохранённых фильтров пока нет
              </div>
            )}
          </div>
        )}
      </Spin>
    </FilterSection>
  );
};

export default memo(SavedFiltersSection);
